import { useRef, useState } from "react"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import {
  parseInvitationImport,
  useImportedTeamResolver,
} from "@/v2-backoffice-client/invitation/create/invitation.create.import"
import { downloadInvitationTemplate } from "@/v2-backoffice-client/invitation/create/invitation.create.template"
import InvitationCreateImportHelp from "@/v2-backoffice-client/invitation/create/InvitationCreateImportHelp"
import {
  sectionMatchStatus,
  type InvitationSection,
} from "@/v2-backoffice-client/invitation/create/invitation.create.sections"
import {
  BULK_ROWS_OPTIONS,
  type InvitationRow,
} from "@/v2-backoffice-client/invitation/invitation.constants"

export type InvitationCreateImportProps = {
  onImported(sections: InvitationSection[]): void
}

const UPLOAD_ACCEPT = ".csv,.tsv,.txt,.xlsx"

/**
 * Reads an uploaded spreadsheet into the paste text: text files verbatim,
 * .xlsx through a lazy-loaded SheetJS (first sheet, tab-separated) so the
 * workbook path costs nothing on the normal bundle. The content lands in
 * the textarea rather than importing straight away — the operator sees
 * exactly what was read before matching runs.
 */
async function readSpreadsheetFile(file: File): Promise<string> {
  if (file.name.toLowerCase().endsWith(".xlsx")) {
    const XLSX = await import("xlsx")
    const workbook = XLSX.read(await file.arrayBuffer())
    const sheet = workbook.Sheets[workbook.SheetNames[0]]
    return XLSX.utils.sheet_to_csv(sheet, { FS: "\t" })
  }
  return file.text()
}

/**
 * The spreadsheet path (FUS-927 CSM ask): paste or upload the whole
 * onboarding list once — email · first name · last name · team, header row
 * optional — and the board builds itself. Team names auto-match existing
 * teams; names with no team match a same-named customer for one-click
 * create+link; only genuinely unresolvable names ask for a decision,
 * flagged per section. HubSpot contact exports work as-is: the company
 * column maps to the team.
 */
function InvitationCreateImport(props: InvitationCreateImportProps) {
  const { onImported } = props
  const [text, setText] = useState("")
  const [resolving, setResolving] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const resolveTeams = useImportedTeamResolver()

  async function loadFile(file: File) {
    try {
      const content = await readSpreadsheetFile(file)
      if (!content.trim()) {
        toast({
          variant: "destructive",
          title: "Empty file",
          description: `${file.name} contained no rows.`,
        })
        return
      }
      setText(content)
      toast({
        title: `Loaded ${file.name}`,
        description: "Review the rows below, then import.",
      })
    } catch {
      toast({
        variant: "destructive",
        title: "Couldn't read the file",
        description: "Save it as .xlsx or .csv and try again.",
      })
    }
  }

  async function runImport() {
    const { invitees, issues } = parseInvitationImport(text)
    if (invitees.length === 0) {
      toast({
        variant: "destructive",
        title: "Nothing to import",
        description: issues.join(" · ") || "No rows with an email found.",
      })
      return
    }

    setResolving(true)
    try {
      const resolutions = await resolveTeams(
        invitees.map((invitee) => invitee.teamName),
      )
      const resolutionByName = new Map(
        resolutions.map((resolution) => [
          resolution.teamName.toLowerCase(),
          resolution,
        ]),
      )

      const byTeam = new Map<string, typeof invitees>()
      for (const invitee of invitees) {
        const key = invitee.teamName.trim().toLowerCase()
        byTeam.set(key, [...(byTeam.get(key) ?? []), invitee])
      }

      const sections: InvitationSection[] = [...byTeam.entries()].map(
        ([key, teamInvitees]) => {
          const resolution = resolutionByName.get(key)
          const rows: InvitationRow[] = teamInvitees.map((invitee) => ({
            ...BULK_ROWS_OPTIONS.createEmptyRow(),
            Email: invitee.email,
            FirstName: invitee.firstName,
            LastName: invitee.lastName,
          }))
          return {
            key: crypto.randomUUID(),
            tenant: resolution?.tenant ?? null,
            rows,
            // Kept on matched sections too: it carries the match-status
            // badge (and the verify toggle) in the form.
            imported: {
              teamName: resolution?.teamName ?? "",
              candidates: resolution?.candidates ?? [],
              // Teams link to customers through the HubSpot id — a
              // matched customer without one cannot be attached, so it
              // is no suggestion.
              suggestedCustomer: resolution?.suggestedCustomer
                ?.ExternalCustomerId
                ? {
                    ExternalCustomerId:
                      resolution.suggestedCustomer.ExternalCustomerId,
                    Name: resolution.suggestedCustomer.Name,
                  }
                : null,
              verified: false,
            },
          }
        },
      )

      const statuses = sections.map(sectionMatchStatus)
      const matched = statuses.filter((s) => s === "matched").length
      const toCreate = statuses.filter((s) => s === "create").length
      const needPick = statuses.length - matched - toCreate
      toast({
        title: `Imported ${invitees.length} invitee(s) across ${sections.length} team(s)`,
        description: [
          matched ? `${matched} matched` : null,
          toCreate ? `${toCreate} will be created on send` : null,
          needPick ? `${needPick} need a pick (flagged below)` : null,
          ...issues,
        ]
          .filter(Boolean)
          .join(" · "),
      })
      onImported(sections)
      setText("")
    } finally {
      setResolving(false)
    }
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-dashed border-border p-3"
      onDragOver={(e) => {
        e.preventDefault()
      }}
      onDrop={(e) => {
        e.preventDefault()
        const file = e.dataTransfer.files?.[0]
        if (file) void loadFile(file)
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">Import from a spreadsheet</span>
        <button
          type="button"
          className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
          data-feature-code="UI_Invitation_Import_HelpToggle"
          onClick={() => {
            setShowHelp((v) => !v)
          }}
        >
          {showHelp ? "Hide help" : "How do I get the list?"}
        </button>
      </div>

      {showHelp && <InvitationCreateImportHelp />}

      <textarea
        className="min-h-16 w-full rounded-md border border-border bg-transparent p-2 font-mono text-xs"
        placeholder={
          "Paste rows: email, first name, last name, team — or drop a file here. HubSpot contact exports work as-is (the company column becomes the team)."
        }
        value={text}
        onChange={(e) => {
          setText(e.target.value)
        }}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={UPLOAD_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void loadFile(file)
          e.target.value = ""
        }}
      />
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          iconSrc="upload"
          isLoading={resolving}
          disabled={!text.trim()}
          onClick={() => void runImport()}
        >
          Import and match teams
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          iconSrc="document"
          onClick={() => {
            fileInputRef.current?.click()
          }}
        >
          Upload file
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          iconSrc="download"
          onClick={() => {
            void downloadInvitationTemplate().catch(() => {
              toast({
                variant: "destructive",
                title: "Template download failed",
                description: "Try again in a moment.",
              })
            })
          }}
        >
          Download template
        </Button>
      </div>
    </div>
  )
}

export default InvitationCreateImport
